import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CAKE_BRANCHES,
  deleteCakeKpi,
  fetchCakeKpis,
  saveCakeKpi,
  unwrapCakeKpiRows,
} from "./cakeDashboardApi";
import { enumerateDateKeys, toDateKey } from "./cakeDashboardUtils";

const EMPTY_FORM = {
  id: null,
  branch: CAKE_BRANCHES.DONG_TIEN.code,
  name: "",
  startDate: toDateKey(),
  endDate: toDateKey(),
  seasonTarget: "",
  productionSeasonTarget: "",
  dailyTargets: {},
  productionDailyTargets: {},
};

const CakeKpiSettings = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadConfigurations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchCakeKpis();
      setConfigurations(unwrapCakeKpiRows(result));
    } catch (loadError) {
      setError(loadError.message || "Không thể tải cấu hình KPI bánh kem.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadConfigurations();
  }, [open, loadConfigurations]);

  const dateKeys = useMemo(
    () => enumerateDateKeys(form.startDate, form.endDate),
    [form.startDate, form.endDate]
  );

  const updateForm = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const startNew = (branch = CAKE_BRANCHES.DONG_TIEN.code) => {
    setForm({ ...EMPTY_FORM, branch });
    setError("");
  };

  const editConfiguration = (configuration) => {
    const dailyTargets = Object.fromEntries(
      (configuration.NgayTargets || []).map((day) => [
        day.Ngay,
        day.KPIBanKemLon == null ? "" : String(day.KPIBanKemLon),
      ])
    );
    const productionDailyTargets = Object.fromEntries(
      (configuration.NgayTargets || []).map((day) => [
        day.Ngay,
        day.KPISanXuatKemLon == null ? "" : String(day.KPISanXuatKemLon),
      ])
    );
    setForm({
      id: configuration.ID,
      branch: configuration.ChiNhanh,
      name: configuration.TenMuaVu || "",
      startDate: configuration.NgayBatDau || toDateKey(),
      endDate: configuration.NgayKetThuc || toDateKey(),
      seasonTarget:
        configuration.KPIBanKemLon == null
          ? ""
          : String(configuration.KPIBanKemLon),
      productionSeasonTarget:
        configuration.KPISanXuatKemLon == null
          ? ""
          : String(configuration.KPISanXuatKemLon),
      dailyTargets,
      productionDailyTargets,
    });
    setError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Tên mùa vụ là bắt buộc.");
      return;
    }
    if (!dateKeys.length) {
      setError("Khoảng ngày mùa vụ không hợp lệ.");
      return;
    }

    const days = dateKeys.map((date) => ({
      Ngay: date,
      KPIBanKemLon:
        form.dailyTargets[date] === "" || form.dailyTargets[date] == null
          ? null
          : Number(form.dailyTargets[date]),
      KPISanXuatKemLon:
        form.branch === CAKE_BRANCHES.BONPAS.code ||
        form.productionDailyTargets[date] === "" ||
        form.productionDailyTargets[date] == null
          ? null
          : Number(form.productionDailyTargets[date]),
    }));
    const dailyTotal = days.reduce(
      (total, day) => total + Number(day.KPIBanKemLon || 0),
      0
    );
    const seasonTarget =
      form.seasonTarget === "" ? dailyTotal || null : Number(form.seasonTarget);
    const productionDailyTotal = days.reduce(
      (total, day) => total + Number(day.KPISanXuatKemLon || 0),
      0
    );
    const productionSeasonTarget =
      form.branch === CAKE_BRANCHES.BONPAS.code
        ? null
        : form.productionSeasonTarget === ""
        ? productionDailyTotal || null
        : Number(form.productionSeasonTarget);

    setSaving(true);
    setError("");
    try {
      await saveCakeKpi({
        ID: form.id,
        TenMuaVu: form.name.trim(),
        ChiNhanh: form.branch,
        NgayBatDau: form.startDate,
        NgayKetThuc: form.endDate,
        KPIBanKemLon: seasonTarget,
        KPISanXuatKemLon: productionSeasonTarget,
        NgayTargets: days,
      });
      await loadConfigurations();
      startNew(form.branch);
      onSaved?.();
    } catch (saveError) {
      setError(saveError.message || "Không thể lưu KPI bánh kem.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configuration) => {
    if (!window.confirm(`Xóa mùa vụ “${configuration.TenMuaVu}”?`)) return;
    setSaving(true);
    setError("");
    try {
      await deleteCakeKpi(configuration.ID);
      await loadConfigurations();
      if (form.id === configuration.ID) startNew(form.branch);
      onSaved?.();
    } catch (deleteError) {
      setError(deleteError.message || "Không thể xóa KPI bánh kem.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="cake-kpi-modal-backdrop" role="presentation">
      <section className="cake-kpi-modal" role="dialog" aria-modal="true">
        <div className="cake-kpi-modal-header">
          <div>
            <p className="cake-eyebrow">Cấu hình từ database</p>
            <h2>KPI bánh kem theo mùa vụ</h2>
          </div>
          <button className="cake-icon-button" onClick={onClose} aria-label="Đóng">
            ×
          </button>
        </div>

        <div className="cake-kpi-settings-grid">
          <aside className="cake-kpi-list">
            <button className="cake-secondary-button" onClick={() => startNew()}>
              + Thêm mùa vụ
            </button>
            {loading ? <p>Đang tải...</p> : null}
            {configurations.map((configuration) => (
              <div
                className={`cake-kpi-list-item ${
                  form.id === configuration.ID ? "active" : ""
                }`}
                key={configuration.ID}
              >
                <button onClick={() => editConfiguration(configuration)}>
                  <strong>{configuration.TenMuaVu}</strong>
                  <span>
                    {CAKE_BRANCHES[configuration.ChiNhanh]?.label ||
                      configuration.ChiNhanh}
                  </span>
                  <small>
                    {configuration.NgayBatDau} → {configuration.NgayKetThuc}
                  </small>
                </button>
                <button
                  className="cake-delete-button"
                  onClick={() => handleDelete(configuration)}
                  aria-label={`Xóa ${configuration.TenMuaVu}`}
                >
                  Xóa
                </button>
              </div>
            ))}
          </aside>

          <form className="cake-kpi-form" onSubmit={handleSave}>
            <div className="cake-form-row">
              <label>
                Đơn vị
                <select
                  value={form.branch}
                  onChange={(event) => updateForm("branch", event.target.value)}
                >
                  <option value={CAKE_BRANCHES.DONG_TIEN.code}>Đồng Tiến</option>
                  <option value={CAKE_BRANCHES.BONPAS.code}>Bonpas</option>
                </select>
              </label>
              <label>
                Tên mùa vụ
                <input
                  value={form.name}
                  onChange={(event) => updateForm("name", event.target.value)}
                  maxLength={100}
                  required
                />
              </label>
            </div>
            <div className="cake-form-row cake-form-row-four">
              <label>
                Từ ngày
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(event) => updateForm("startDate", event.target.value)}
                  required
                />
              </label>
              <label>
                Đến ngày
                <input
                  type="date"
                  value={form.endDate}
                  min={form.startDate}
                  onChange={(event) => updateForm("endDate", event.target.value)}
                  required
                />
              </label>
              <label>
                KPI bán mùa
                <input
                  type="number"
                  min="0"
                  value={form.seasonTarget}
                  placeholder="Tự cộng KPI ngày"
                  onChange={(event) => updateForm("seasonTarget", event.target.value)}
                />
              </label>
              <label>
                KPI sản xuất mùa
                <input
                  type="number"
                  min="0"
                  value={form.productionSeasonTarget}
                  placeholder="Tự cộng KPI ngày"
                  disabled={form.branch === CAKE_BRANCHES.BONPAS.code}
                  onChange={(event) =>
                    updateForm("productionSeasonTarget", event.target.value)
                  }
                />
              </label>
            </div>

            <div className="cake-daily-targets">
              <div className="cake-daily-targets-header">
                <strong>KPI từng ngày</strong>
                <span>Ngày · Bán · Sản xuất</span>
              </div>
              {dateKeys.map((date) => (
                <label key={date}>
                  <span>{new Date(`${date}T00:00:00`).toLocaleDateString("vi-VN")}</span>
                  <input
                    type="number"
                    min="0"
                    value={form.dailyTargets[date] ?? ""}
                    placeholder="Chưa đặt KPI"
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        dailyTargets: {
                          ...current.dailyTargets,
                          [date]: event.target.value,
                        },
                      }))
                    }
                  />
                  <input
                    type="number"
                    min="0"
                    value={form.productionDailyTargets[date] ?? ""}
                    placeholder="KPI sản xuất"
                    disabled={form.branch === CAKE_BRANCHES.BONPAS.code}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        productionDailyTargets: {
                          ...current.productionDailyTargets,
                          [date]: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
              ))}
            </div>

            {error ? <div className="cake-inline-error">{error}</div> : null}
            <div className="cake-form-actions">
              <button type="button" className="cake-secondary-button" onClick={onClose}>
                Đóng
              </button>
              <button type="submit" className="cake-primary-button" disabled={saving}>
                {saving ? "Đang lưu..." : "Lưu KPI"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CakeKpiSettings;
